// teamColor: deterministic hash → HSL palette per team name, with optional
// explicit-hex override.
//
// The hash function (FNV-1a 32-bit, plus a final mix step to spread short
// inputs) maps a team name to a stable hue in [0, 360). Same name → same
// color, every reload, no DB write needed for the common case. Saturation
// and lightness are fixed so the palette feels consistent across the app
// — different team names, different hues, but uniform "weight."
//
// `derive` returns the auto color. `resolve` takes a possibly-overridden
// value (the team's `color` field from the API, "" when no override) and
// returns the same shape — call sites stay agnostic about whether the
// color is auto or explicit.
//
// Keep this file dependency-free and tree-shakeable; it gets imported into
// any view that wants to "indicate the team" (members table cell, schedule
// chip, badge in the activity feed, etc.).

// Tuned for the SPA's ink-on-soft palette. Background is a pastel; the
// foreground we use on top is `--ink` (#26272B), which stays readable at
// these L values for any hue.
const PALETTE_S = 62;
const PALETTE_L_BG = 86;
const PALETTE_L_BORDER = 60;
const PALETTE_L_DOT = 55;

// FNV-1a 32-bit hash. Stable, no deps. Final xor-shift spreads the low
// bits so short names like "A" / "B" don't cluster on adjacent hues.
function fnv1a(input: string): number {
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    // Math.imul for 32-bit overflow correctness.
    h = Math.imul(h, 0x01000193);
  }
  // Final mix — bumps avalanche so 1-letter inputs spread.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  return h >>> 0;
}

// Normalised key — trim + lowercase so "U13", " U13 ", "u13" all share a
// hue. NFC just in case a renamed team picks up combining marks; harmless
// on ASCII.
function keyOf(name: string): string {
  return name.trim().toLowerCase().normalize('NFC');
}

// hueFor returns the deterministic hue for a team name in [0, 360).
function hueFor(name: string): number {
  return fnv1a(keyOf(name)) % 360;
}

// hslToHex converts an HSL triplet to a 7-char lowercase hex string. Same
// math the CHECK constraint on members.team.color expects on the way in.
function hslToHex(h: number, s: number, l: number): string {
  const sFrac = s / 100;
  const lFrac = l / 100;
  const k = (n: number): number => (n + h / 30) % 12;
  const a = sFrac * Math.min(lFrac, 1 - lFrac);
  const f = (n: number): number => {
    const v = lFrac - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
    return Math.round(255 * v);
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

export interface TeamColorPalette {
  // hex string — the canonical color for the team (override or derived).
  hex: string;
  // Pastel background suitable for chips / pills / accordion banners.
  bg: string;
  // Mid-tone for borders and the small "team dot" in lists.
  border: string;
  // Saturated dot — a single small swatch. Same hue as `bg` but heavier.
  dot: string;
  // True when the color was auto-derived from name (no explicit override).
  // Useful when the SPA wants to render an "auto" hint in an editor.
  isAuto: boolean;
}

// derive returns the auto-generated palette for a team name. Use only when
// you don't have a Team object in hand (e.g. a brand-new team-name input
// previewing color before submit). For existing teams, use `resolve`.
export function derive(name: string): TeamColorPalette {
  const hue = hueFor(name);
  return {
    hex: hslToHex(hue, PALETTE_S, PALETTE_L_DOT),
    bg: hslToHex(hue, PALETTE_S, PALETTE_L_BG),
    border: hslToHex(hue, PALETTE_S, PALETTE_L_BORDER),
    dot: hslToHex(hue, PALETTE_S, PALETTE_L_DOT),
    isAuto: true,
  };
}

// resolve returns the palette for a team, honoring an explicit hex
// override when present. The override is trusted to be valid — server
// rejects malformed values via the CHECK constraint, so anything reaching
// here passed validation.
export function resolve(name: string, override: string | null | undefined): TeamColorPalette {
  const trimmed = (override ?? '').trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(trimmed)) {
    return derive(name);
  }
  // Override case: derive border / dot variants from the user's chosen hex
  // by interpreting it as the "border" color and synthesising the others.
  // Cheaper than a roundtrip through HSL — we want consistent visual
  // weight, not a custom palette generator.
  return {
    hex: trimmed,
    // Dim the override by mixing toward white for a soft background.
    bg: mixWithWhite(trimmed, 0.78),
    border: trimmed,
    dot: trimmed,
    isAuto: false,
  };
}

// mixWithWhite returns hex blended `t` of the way toward white (1.0 = pure
// white, 0.0 = original). Used to synthesise a soft background from a
// user-chosen accent color without forcing them to pick three values.
function mixWithWhite(hex: string, t: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}
